/**
 * Call Event Constants
 * Standardized event names for the call lifecycle across
 * Chat UI ↔ Socket Server ↔ Meet Service
 *
 * TypeScript mirror of freiburger-socket/src/constants/callEvents.js
 */

// Client → Socket Server
export const CALL_CLIENT_EVENTS = {
  START_CALL: "start_call",
  ACCEPT_CALL: "accept_call",
  REJECT_CALL: "reject_call",
  LEAVE_CALL: "leave_call"
} as const;

// Socket Server → Client
export const CALL_SERVER_EVENTS = {
  INCOMING_CALL: "incoming_call",
  CALL_ACCEPTED: "call_accepted",
  CALL_STARTED: "call_started",
  USER_JOINED: "user_joined",
  USER_LEFT: "user_left",
  CALL_ENDED: "call_ended",
  CALL_MISSED: "call_missed",
  CALL_STATE_SYNC: "call_state_sync"
} as const;

// Meet Service → Socket Server (via webhook)
export const CALL_WEBHOOK_EVENTS = {
  PARTICIPANT_JOINED: "PARTICIPANT:JOINED",
  PARTICIPANT_LEFT: "PARTICIPANT:LEFT",
  MEETING_LEFT: "MEETING:LEFT",
  MEETING_ENDED: "MEETING:ENDED"
} as const;

// Call session statuses
export const CALL_STATUS = {
  RINGING: "ringing",
  ACCEPTED: "accepted",
  ACTIVE: "active",
  ENDED: "ended",
  MISSED: "missed"
} as const;

// Call types
export const CALL_TYPE = {
  ONE_TO_ONE: "1:1",
  GROUP: "group"
} as const;

// End reasons
export const CALL_END_REASON = {
  NORMAL: "normal",
  DECLINED: "declined",
  CALLER_LEFT: "caller_left",
  TIMEOUT: "timeout",
  LAST_PARTICIPANT: "last_participant",
  MEETING_ENDED: "meeting_ended"
} as const;

// Derived types
export type CallClientEvent = (typeof CALL_CLIENT_EVENTS)[keyof typeof CALL_CLIENT_EVENTS];
export type CallServerEvent = (typeof CALL_SERVER_EVENTS)[keyof typeof CALL_SERVER_EVENTS];
export type CallStatus = (typeof CALL_STATUS)[keyof typeof CALL_STATUS];
export type CallType = (typeof CALL_TYPE)[keyof typeof CALL_TYPE];
export type CallEndReason = (typeof CALL_END_REASON)[keyof typeof CALL_END_REASON];

// Payload types for server → client events
export interface IncomingCallPayload {
  fromUserId: string;
  fromUserName: string;
  fromUserAvatar: string | null;
  type: "audio" | "video";
  chatId: string;
  roomName: string;
  callId: string;
  callType: CallType;
  startTime: number;
}

export interface CallAcceptedPayload {
  chatId: string;
  callId: string;
  roomName: string;
  type: "audio" | "video";
  startTime: number;
}

export interface CallStartedPayload {
  chatId: string;
  callId: string;
  roomName?: string;
  type?: "audio" | "video";
  status?: string;
  startTime: number;
}

export interface CallEndedPayload {
  chatId: string;
  callId: string;
  duration?: number;
  reason: CallEndReason;
}

export interface CallMissedPayload {
  chatId: string;
  callId: string;
  roomName?: string;
}

export interface UserJoinedPayload {
  chatId: string;
  callId: string;
  userId: string;
  userName?: string;
}

export interface UserLeftPayload {
  chatId: string;
  callId: string;
  userId: string;
}

export interface CallStateSyncPayload {
  callId: string;
  roomName: string;
  chatId: string;
  type: "audio" | "video";
  callType: CallType;
  status: CallStatus;
  callerId: string;
  participants: string[];
  joinedParticipants: string[];
  startTime: number;
  acceptedAt?: number;
}
